export const foundations = {
  name: 'Little Splash Foundations™',
  method: 'Powered by the LSF Method™, the Little Splash approach to early water safety.',
  subtitle: 'Early Water Safety',
  ages: '1 – 3 years',
  tagline: "40 objectives. 12 weeks. They won't just wait for help — they'll know exactly what to do.",
  pillars: [
    {
      id: 'objectives', title: '40 Objectives',
      lead: 'A clear, step-by-step skill map — so nothing is skipped and nothing is rushed.',
      points: [
        'Water comfort & trust: calm entries, face wetting, relaxed body in the water',
        'Breath control: blowing bubbles, holding breath, and safe, gentle submersions',
        'Floating: supported to independent back float, plus rolling from tummy to back',
        'Wall & exit skills: turning to the wall, holding on, and climbing out safely',
        'Water-safety habits: waiting for permission and understanding pool boundaries',
      ],
      close: 'Each objective builds on the one before it. Your child only moves forward when they are comfortable and consistent.',
    },
    {
      id: 'weeks', title: '12 Weeks',
      lead: 'A focused timeline with a goal — never a deadline.',
      points: [
        'Weeks 1–4 · Acclimation: 1 lesson per week to build trust and comfort',
        'Weeks 5–12 · Acceleration: 2 lessons per week to build real skills quickly',
        '20 private lessons total, 20 minutes each',
        'Starts the first week of the month you enroll',
      ],
      close: 'Generally, all students are proficient by 12 weeks. If your child needs more time, we will work together on the next best step.',
    },
    {
      id: 'progress', title: 'Transparent Progress',
      lead: 'You will always know where your child is and what comes next.',
      points: [
        'Every one of the 40 objectives is tracked lesson by lesson',
        'Regular progress updates from your coach — no guessing',
        'Honest feedback on what is going well and what we are working on',
        'Tips you can practice during bath time and pool days',
      ],
      close: 'Progress is celebrated, never compared. Every child moves at their own pace.',
    },
    {
      id: 'skills', title: 'Real Water-Safety Skills',
      lead: 'Skills that help them know what to do in the water — taught without fear.',
      points: [
        'Rolling to a back float and resting on their back to breathe',
        'Turning around and getting back to the wall',
        'Holding on and climbing out of the pool',
        'Respecting water boundaries — no more unexpected jumps',
      ],
      close: 'Water-safety skills are never a substitute for constant, close adult supervision.',
    },
  ],
  perLesson: '$60',
  firstMonth: '$240',
  laterMonths: '$480',
  total: '$1,200',
  priceLine: '$60/lesson. $240 to start, then $480/month for months 2 and 3.',
  lessons: 20,
  minutes: 20,
  seats: 8,
  waitlistFee: '$25',
  phases: [
    { name: 'Acclimation', when: 'Weeks 1 – 4 · Month 1', freq: '1 lesson / week', lessons: 4, body: 'Building trust, comfort, and familiarity with the water, the pool, and their instructor. Play first, always.' },
    { name: 'Acceleration', when: 'Weeks 5 – 12 · Months 2 – 3', freq: '2 lessons / week', lessons: 16, body: 'With comfort in place, we pick up the pace — breath control, back floating, rolling to float, and safe entries and exits.' },
  ],
  whoFor: [
    'Little ones ages 1 to 3',
    'Children who are new to the water, or who are nervous around it',
    'Families who want real water-safety skills without forcing or rushing',
    'Parents who want clear, honest updates on how their child is progressing',
  ],
  after: 'Following completion, most swimmers are ready for group lessons.',
};

export const foundationsComparison = {
  intro: "Families often ask how Little Splash Foundations™ compares to ISR (Infant Swimming Resource). ISR has helped many children, and we deeply respect our shared goal: fewer drownings. Our approaches differ in pace and philosophy, so here's an honest side-by-side to help you choose what fits your family best.",
  rows: [
    { topic: 'Lesson schedule', other: 'Short lessons (about 10 minutes), typically 5 days a week for several weeks.', ours: '20-minute private lessons — once a week in month 1, then twice a week in months 2–3.' },
    { topic: 'Pace', other: 'Intensive, daily repetition from the very first week.', ours: 'Acclimation first, then acceleration once your child feels comfortable and safe.' },
    { topic: 'Feelings & tears', other: 'Some upset is often considered an expected part of the process.', ours: 'When a child says "I\'m not ready," we pause, comfort, and make a plan. Play-based, never forced.' },
    { topic: 'Skills focus', other: 'Centered on a single float sequence.', ours: '40 objectives: back floating plus water comfort, breath control, safe entries & exits, and a love of the water.' },
    { topic: 'Progress', other: 'Varies by instructor.', ours: 'Transparent progress across all 40 objectives, shared with you along the way.' },
  ],
  note: 'Details for other providers vary by instructor and location — we encourage families to ask them directly.',
};

export const foundationsFaq = [
  {
    category: 'About the Program',
    items: [
      { q: 'Who is Little Splash Foundations™ for?', a: 'Little Splash Foundations™ is designed for little ones ages 1 to 3 — whether they are brand new to the water or a bit nervous around it. It is built for families who want real water-safety skills without fear-based methods.' },
      { q: 'How are lessons structured?', a: 'Foundations is 20 private (1:1) lessons, 20 minutes each, over 12 weeks. Weeks 1–4 (Acclimation) meet once a week. Weeks 5–12 (Acceleration) meet twice a week.' },
      { q: 'Why private 1:1 lessons?', a: 'Toddlers learn best with one trusted adult giving them full attention. Private lessons let us follow your child’s cues, adjust in the moment, and build skills safely and quickly.' },
      { q: 'Why does the schedule speed up after month 1?', a: 'Month 1 is all about trust and comfort. Once your child feels safe, meeting twice a week helps new skills stick and builds momentum.' },
      { q: 'How is Little Splash Foundations™ different from First Splash?', a: 'First Splash (8–12 months) is a Parent & Me introduction to the water. Little Splash Foundations™ (ages 1–3) is a structured 12-week, 1:1 program focused on real water-safety skills.' },
    ],
  },
  {
    category: 'Pricing & Enrollment',
    items: [
      { q: 'What does it cost?', a: 'Little Splash Foundations™ is $60 per private lesson — $1,200 total for 20 lessons. You pay $240 for month 1 (4 lessons), then $480 per month for months 2 and 3 (8 lessons each).' },
      { q: 'How does enrollment work?', a: 'Foundations enrollment opens monthly on the Sunday before the 1st, for swimmers starting their 12-week spot the first week of that month. The first opening is Sunday, November 29, 2026 for December starts.' },
      { q: 'How many spots are available?', a: 'Each start is capped at 8 seats so every swimmer gets the attention they deserve. The live seat count is shown on this page.' },
      { q: 'What if seats are full?', a: 'A $25 waitlist fee holds your place in line. We’ll reach out as soon as a seat opens for your child.' },
      { q: 'I’m on the waitlist — what are the next steps?', a: 'A member of our team will be in touch shortly to confirm your spot on the waitlist and let you know where you are in line! You will be notified when you are next in line — and then again when a spot is open. After a spot opens, you will have 7 days to enroll before the spot is released to the next person waiting. We recommend completing your swim assessment, along with joining any First Splash lessons you can while you wait! All families on the Foundations waitlist are welcome to book First Splash classes as an introduction to our pool and instructors. 🤍' },
    ],
  },
  {
    category: 'Progress & Feelings',
    items: [
      { q: 'What if my child can not finish in 12 weeks?', a: "We will NOT force them to meet our timelines. Every child is different, and 12 weeks is our goal. If that doesn't work out, we will work together to figure out the next steps to best support your child. Generally, all students are proficient by 12 weeks, with nearly all being proficient by 16 weeks. Graduation is based on the 40-objective checklist, and a 16-week flex window covers pauses for illness." },
      { q: 'What are the 40 objectives?', a: 'They are the step-by-step skills we track for every swimmer — from water comfort and breath control to back floating, rolling to float, and safe entries and exits. You will always know where your child is and what comes next.' },
      { q: 'What if my child cries or gets upset?', a: "That's okay — their feelings matter. We slow down, comfort, and use play to rebuild trust. We never push a child through fear, because early experiences shape how they feel about water for years to come." },
      { q: 'How will I know how my child is doing?', a: 'Progress across all 40 objectives is tracked and shared with you along the way, so you always know what your child has mastered and what is next.' },
    ],
  },
  {
    category: 'After Little Splash Foundations™',
    items: [
      { q: 'What happens after Little Splash Foundations™?', a: 'Following completion, most swimmers are ready for group lessons. Coach Abrie will recommend the best next step for your child — usually our Group Membership.' },
      { q: 'Does my child need to be 3 for group lessons?', a: 'Group Membership is designed for ages 3–12. If your child finishes Foundations before turning 3, we’ll talk through the best option together.' },
    ],
  },
];

export const firstSplash = {
  ages: '8 – 12 months',
  price: '$25',
  whoFor: [
    'Babies roughly 8 to 12 months old, with a parent or caregiver in the water',
    'Families looking for a gentle, joyful first experience in the pool',
    'Parents who want to learn safe holds and water play they can use anywhere',
  ],
  includes: [
    'Songs, splashing, floating, and bonding in our heated indoor pool',
    'Safe holds and positions for parents',
    'Small, calm class — one class in the pool at a time',
    '$25 per session, book only what works for you',
  ],
  faq: [
    { q: 'When are classes posted?', a: 'The First Splash calendar is posted monthly on the Sunday before the 1st, and that month’s spots open up at the same time.' },
    { q: 'Do I need to get in the water?', a: 'Yes — First Splash is a Parent & Me class, so one parent or caregiver joins their baby in the water.' },
    { q: 'What if no dates work?', a: 'Join the priority waitlist and we’ll reach out as soon as next month’s spots open.' },
    { q: 'What comes next?', a: 'Around 12 months, many families move into Little Splash Foundations™, our 12-week early water-safety program.' },
    { q: 'How much does it cost?', a: 'First Splash is $25 per session. Book only the classes that work for your family.' },
    { q: 'When is the first release?', a: 'The first First Splash calendar is released Sunday, November 29, 2026 for December classes. After that, new classes are released the Sunday before the 1st of every month.' },
    { q: 'Is the pool warm enough for babies?', a: 'Yes — our private indoor pool is heated, and only one class is in the water at a time, so it stays calm and comfortable.' },
    { q: 'What will we do in class?', a: 'Songs, splashing, gentle floating, safe holds, and lots of bonding. It is all play-based and baby-led.' },
  ],
};

export const groupMembership = {
  ages: '3 – 12 years',
  lead: 'Small, confidence-first group lessons — billed simply as a monthly membership.',
  facts: ['Max 4 swimmers per class', 'One class in the pool at a time', 'Heated indoor pool', '$20 sibling discount after enrollment'],
};

export const privateLessons = {
  options: [
    { id: 'private', title: 'Private (1:1)', body: 'One swimmer, one coach, a fully customized plan. Great for swimmers who need focused attention or want to progress faster.' },
    { id: 'semi', title: 'Semi-Private (2:1)', body: 'Two swimmers, one coach. Reserved for siblings or friends within 3 years of age, or children who need closer attention (focus, sensory needs, high anxiety).' },
  ],
  note: 'Private lessons are often at capacity. Reach out and we’ll let you know what’s available.',
  faq: [
    { q: 'Who needs private or semi-private lessons?', a: 'Swimmers who benefit from extra attention: children with high water anxiety, sensory or attention needs, swimmers who need a slower or faster pace than a group, and older beginners who prefer a more personal setting.' },
    { q: 'My child struggles in big, loud swim lessons. Can they still succeed here?', a: 'Yes — and this is where our space really shines. We run only one class in the pool at a time, in a private heated indoor pool. There are no competing lanes, whistles, or crowds, so many swimmers who couldn’t traditionally participate in group lessons do beautifully here.' },
    { q: 'Can private lessons lead into group lessons?', a: 'Absolutely. Many swimmers start private or semi-private to build comfort and confidence, then move into our Group Membership once they’re ready.' },
    { q: 'Who can share a semi-private lesson?', a: 'Semi-privates pair two swimmers — usually siblings or friends within 3 years of age — or are used for children who need closer attention than a group of four allows.' },
    { q: 'How long are lessons?', a: 'Lessons are typically 20–30 minutes so swimmers stay engaged and focused the whole time.' },
    { q: 'How do I get started?', a: 'Private spots are limited and often full. Contact us and we’ll let you know current availability, or add you to the private lessons waitlist.' },
  ],
};
